import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  MessageSquare, Settings, CheckCircle, XCircle, Send, 
  Phone, ExternalLink, Copy, AlertCircle, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  const [config, setConfig] = useState({
    configured: false,
    enabled: false,
    whatsapp_number: '',
    account_sid_masked: '',
    sandbox_mode: true
  });
  
  const [credentials, setCredentials] = useState({
    account_sid: '',
    auth_token: '',
    whatsapp_number: '',
    webhook_url: `${BACKEND_URL}/api/twilio-whatsapp/webhook`,
    sandbox_mode: true
  });
  
  const [testMessage, setTestMessage] = useState({
    to_phone: '',
    message: 'Hello from Treppan CRM! This is a test message.'
  });
  
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (token) {
      setLoading(true);
      fetchConfig();
    }
  }, [token]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/twilio-whatsapp/config?token=${token}`);
      setConfig(response.data);
      if (response.data.configured) {
        setCredentials(prev => ({
          ...prev,
          whatsapp_number: response.data.whatsapp_number || '',
          webhook_url: response.data.webhook_url || `${BACKEND_URL}/api/twilio-whatsapp/webhook`,
          sandbox_mode: response.data.sandbox_mode ?? true
        }));
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!credentials.account_sid || !credentials.auth_token || !credentials.whatsapp_number) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/twilio-whatsapp/configure?token=${token}`, credentials);
      
      if (response.data.success) {
        toast.success('WhatsApp configuration saved successfully!');
        fetchConfig();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/twilio-whatsapp/test-connection?token=${token}`);
      
      if (response.data.success) {
        toast.success(`Connected! Account: ${response.data.account_name}`);
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testMessage.to_phone || !testMessage.message) {
      toast.error('Please enter phone number and message');
      return;
    }
    
    setSendingTest(true);
    try {
      const response = await axios.post(`${API}/twilio-whatsapp/send?token=${token}`, {
        to_phone: testMessage.to_phone,
        message: testMessage.message
      });
      
      if (response.data.success) {
        toast.success(`Message sent! SID: ${response.data.message_sid}`);
      } else {
        toast.error(response.data.error || 'Failed to send message');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(credentials.webhook_url);
    toast.success('Webhook URL copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="whatsapp-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="text-green-500" />
            WhatsApp Integration
          </h1>
          <p className="text-muted-foreground">Configure Twilio WhatsApp for messaging</p>
        </div>
        <Badge variant={config.configured ? "success" : "secondary"}>
          {config.configured ? (
            <><CheckCircle size={14} className="mr-1" /> Connected</>
          ) : (
            <><XCircle size={14} className="mr-1" /> Not Configured</>
          )}
        </Badge>
      </div>

      <Tabs defaultValue="setup" className="space-y-4">
        <TabsList>
          <TabsTrigger value="setup">Setup</TabsTrigger>
          <TabsTrigger value="test" disabled={!config.configured}>Test</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="setup" className="space-y-4">
          {/* Setup Instructions */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Before you start:</strong> You need a Twilio account with WhatsApp enabled.
              <a 
                href="https://www.twilio.com/try-twilio" 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-1 text-primary hover:underline inline-flex items-center"
              >
                Sign up for Twilio <ExternalLink size={12} className="ml-1" />
              </a>
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings size={20} />
                Twilio Credentials
              </CardTitle>
              <CardDescription>
                Enter your Twilio Account SID and Auth Token from the Twilio Console
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_sid">Account SID *</Label>
                  <Input
                    id="account_sid"
                    data-testid="twilio-account-sid"
                    value={credentials.account_sid}
                    onChange={(e) => setCredentials(prev => ({ ...prev, account_sid: e.target.value }))}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <p className="text-xs text-muted-foreground">Found on Twilio Console Dashboard</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="auth_token">Auth Token *</Label>
                  <Input
                    id="auth_token"
                    data-testid="twilio-auth-token"
                    type="password"
                    value={credentials.auth_token}
                    onChange={(e) => setCredentials(prev => ({ ...prev, auth_token: e.target.value }))}
                    placeholder="Your Twilio Auth Token"
                  />
                  <p className="text-xs text-muted-foreground">Click to reveal in Twilio Console</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="whatsapp_number">WhatsApp Number *</Label>
                <Input
                  id="whatsapp_number"
                  data-testid="twilio-whatsapp-number"
                  value={credentials.whatsapp_number}
                  onChange={(e) => setCredentials(prev => ({ ...prev, whatsapp_number: e.target.value }))}
                  placeholder="+14155238886 (Sandbox) or your business number"
                />
                <p className="text-xs text-muted-foreground">
                  For sandbox testing, use: <code className="bg-muted px-1 rounded">+14155238886</code>
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="sandbox_mode"
                  checked={credentials.sandbox_mode}
                  onCheckedChange={(checked) => setCredentials(prev => ({ ...prev, sandbox_mode: checked }))}
                />
                <Label htmlFor="sandbox_mode">Sandbox Mode (for testing)</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveConfig} disabled={saving} data-testid="save-whatsapp-config">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Configuration
                </Button>
                
                {config.configured && (
                  <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
                    {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Test Connection
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sandbox Instructions */}
          {credentials.sandbox_mode && (
            <Card>
              <CardHeader>
                <CardTitle>Sandbox Setup</CardTitle>
                <CardDescription>Follow these steps to enable WhatsApp sandbox</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Go to <a href="https://console.twilio.com/us1/develop/sms/try-it-out/whatsapp-learn" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Twilio WhatsApp Sandbox</a></li>
                  <li>Send the join code from your WhatsApp to the sandbox number</li>
                  <li>Example: Send <code className="bg-muted px-1 rounded">join [your-code]</code> to +1 415 523 8886</li>
                  <li>Once joined, you can send/receive messages for 72 hours</li>
                </ol>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="test" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send size={20} />
                Send Test Message
              </CardTitle>
              <CardDescription>
                Test your WhatsApp integration by sending a message
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test_phone">Recipient Phone Number</Label>
                <Input
                  id="test_phone"
                  data-testid="test-phone-number"
                  value={testMessage.to_phone}
                  onChange={(e) => setTestMessage(prev => ({ ...prev, to_phone: e.target.value }))}
                  placeholder="+971501234567"
                />
                <p className="text-xs text-muted-foreground">
                  Must be a number that has joined your sandbox (if in sandbox mode)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="test_message">Message</Label>
                <Input
                  id="test_message"
                  data-testid="test-message"
                  value={testMessage.message}
                  onChange={(e) => setTestMessage(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Hello from Treppan CRM!"
                />
              </div>

              <Button onClick={handleSendTestMessage} disabled={sendingTest} data-testid="send-test-btn">
                {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                Send Test Message
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Webhook Configuration</CardTitle>
              <CardDescription>
                Configure these URLs in your Twilio Console to receive incoming messages
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Incoming Message Webhook URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={credentials.webhook_url}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" size="icon" onClick={copyWebhookUrl}>
                    <Copy size={16} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Set this as your WhatsApp webhook URL in Twilio Console
                </p>
              </div>

              <div className="space-y-2">
                <Label>Status Callback URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={`${credentials.webhook_url}/status`}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" size="icon" onClick={() => {
                    navigator.clipboard.writeText(`${credentials.webhook_url}/status`);
                    toast.success('Status callback URL copied');
                  }}>
                    <Copy size={16} />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  For delivery status updates (sent, delivered, read)
                </p>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>How to configure:</strong>
                  <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                    <li>Go to Twilio Console → Messaging → Senders → WhatsApp Senders</li>
                    <li>Select your WhatsApp number</li>
                    <li>Under "A message comes in", paste the webhook URL</li>
                    <li>Set method to POST</li>
                  </ol>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WhatsAppSettings;
